import { request } from '../api/client';

/**
 * Разбор нераспознанных наименований.
 *
 * <p>Написание, не совпавшее с эталоном, приёмку не останавливает — карточка
 * заводится с написанием как есть и без категории. Разгребают такие вечером,
 * а после импорта чужого склада их набирается сразу сотня.
 *
 * <p>Ни кэша, ни очереди, как и у продажи: справочник правят за столом,
 * и сопоставление, отложенное в телефоне, ничего не чинит. Вдобавок оно
 * меняет вид детали и категорию всех позиций под написанием разом — такое не должно
 * происходить вслепую из очереди.
 */

export interface UnmatchedName {
  id: number;
  name: string;
  matchStatus: string;
  partKindId: number | null;
  categoryId: number | null;
  /** Сколько позиций заведено под этим написанием: что чинить раньше. */
  usageCount: number;
  /** Заголовок одной из карточек: по нему видно, что за деталь под этим написанием. */
  sampleTitle: string | null;
  createdAt: string;
}

/** Эталонный вид детали из общего каталога. */
export interface PartKind {
  id: number;
  categoryId: number | null;
  name: string;
}

export interface UnmatchedPage {
  items: UnmatchedName[];
  total: number;
}

export function unmatchedNames(page = 0, size = 20): Promise<UnmatchedPage> {
  return request<UnmatchedPage>(`/api/part-names/unmatched?page=${page}&size=${size}`);
}

/** Похожие эталоны. Решает человек — алгоритм только предлагает. */
export function suggestionsFor(partNameId: number): Promise<PartKind[]> {
  return request<PartKind[]>(`/api/part-names/${partNameId}/suggestions`);
}

/**
 * Поиск эталона руками.
 *
 * <p>Подсказки идут по похожести строк, а «запаска» не похожа на «Колесо
 * запасное» ничем. Без поиска разбор встанет на первом же таком написании.
 */
/**
 * Весь справочник видов деталей.
 *
 * <p>Отдельно от поиска: экрану отбора выгрузки нужны названия уже выбранных
 * видов, а поиск по идентификатору не ищет. Строк сто семьдесят восемь,
 * справочник меняется с релизом — брать его целиком дешевле, чем гонять
 * запрос на каждую букву.
 */
export function allKinds(): Promise<PartKind[]> {
  return request<PartKind[]>('/api/part-names/kinds/all');
}

/**
 * Поиск эталона руками — вместе с числом найденного.
 *
 * <p>Выдача обрезана двумя десятками: по слову «датчик» эталонов 21, и один
 * не показан вовсе. Разбирающий, не найдя нужного, решает, что такого эталона
 * нет, — а одно сопоставление правит сотни карточек и назад не откатывается.
 */
export function searchKinds(query: string): Promise<{ items: PartKind[]; total: number }> {
  return request<{ items: PartKind[]; total: number }>(
    `/api/part-names/kinds?q=${encodeURIComponent(query)}`);
}

export interface MatchResult {
  partName: UnmatchedName;
  /** Сколько карточек получили категорию и вид детали (название не меняется). */
  updated: number;
}

export function matchName(partNameId: number, partKindId: number): Promise<MatchResult> {
  return request<MatchResult>(`/api/part-names/${partNameId}/match`, {
    method: 'POST',
    body: { partKindId },
  });
}

/** Написание, уже сведённое с эталоном, — то, с чего снимают сопоставление. */
export interface MatchedName {
  partName: UnmatchedName;
  /** Эталон словом. `null` — такого эталона в справочнике больше нет. */
  kindName: string | null;
}

export interface MatchedPage {
  items: MatchedName[];
  total: number;
}

/**
 * Сопоставленные написания — список, с которого снимают ошибочный эталон.
 *
 * <p>Экран разбора показывал только нераспознанные, то есть сведённое
 * с эталоном человеку не показывалось вовсе: сопоставить он мог, отменить
 * нет. А после переезда клиента таких сопоставлений сотни, и ошибочное
 * означает деталь, уехавшую в объявление под чужим наименованием.
 */
export function matchedNames(page = 0, size = 20): Promise<MatchedPage> {
  return request<MatchedPage>(`/api/part-names/matched?page=${page}&size=${size}`);
}

/**
 * Снимает сопоставление: эталон оказался не тем.
 *
 * <p>Карточки под написанием не меняются вовсе — вид детали и категория
 * остаются теми, что поставило сопоставление, — а само написание
 * возвращается в список нераспознанных. Экран говорит это словами:
 * снятие, после которого непонятно, что изменилось, вернёт человека
 * к разработчику.
 */
export function unmatchName(partNameId: number): Promise<UnmatchedName> {
  return request<UnmatchedName>(`/api/part-names/${partNameId}/unmatch`, { method: 'POST' });
}

/**
 * Пересопоставляет нераспознанные написания по нынешнему справочнику.
 *
 * <p>Справочник видов деталей растёт с релизом, а написания клиента заведены
 * раньше: без этого пополнение не меняет ничего, и владелец продолжает видеть
 * ту же стену нераспознанных. Точное совпадение с эталоном или синонимом,
 * похожесть сюда не идёт; решённое человеком не трогается.
 *
 * @returns сколько написаний нашли эталон и сколько карточек это исправило
 */
export function rematchNames(): Promise<{ matched: number; updated: number }> {
  return request<{ matched: number; updated: number }>('/api/part-names/rematch', {
    method: 'POST',
  });
}
